import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Skeleton,
  Stack,
  Alert,
  Button,
  type SelectChangeEvent,
} from '@mui/material';
import { format } from 'date-fns';
import { useTimeEntries, type TimeEntryFilters } from '@/hooks';
import { useMechanics } from '@/hooks/useStaffUsers';
import { DataTable, type DataTableColumn, UserAvatar } from '@/components/common';
import type { TimeEntry, TimeEntryType } from '@/types';
import { dateFnsLocale } from '@/utils/locale';

const entryTypeColor: Record<TimeEntryType, 'success' | 'error' | 'warning' | 'info'> = {
  clock_in: 'success',
  clock_out: 'error',
  break_start: 'warning',
  break_end: 'info',
  lunch_start: 'warning',
  lunch_end: 'info',
};

export function WorkHoursTab() {
  const { t } = useTranslation('hr');
  const { t: tc } = useTranslation('common');

  const [employeeFilter, setEmployeeFilter] = useState<string | 'all'>('all');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  const { data: mechanics = [] } = useMechanics();

  const filters: TimeEntryFilters = {
    employeeId: employeeFilter !== 'all' ? employeeFilter : undefined,
    from: dateFrom,
    to: dateTo,
    limit: 100,
  };

  const { data: entriesData, isLoading, isError, refetch } = useTimeEntries(filters);

  const entries: TimeEntry[] = useMemo(
    () => entriesData?.pages.flatMap((p) => p.data) ?? [],
    [entriesData]
  );

  const sortCompareFn = (a: TimeEntry, b: TimeEntry, key: string): number => {
    switch (key) {
      case 'timestamp':
        return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      case 'employee':
        return (a.employeeName ?? '').localeCompare(b.employeeName ?? '', 'fi');
      case 'type':
        return a.type.localeCompare(b.type);
      default:
        return 0;
    }
  };

  const columns: DataTableColumn<TimeEntry>[] = [
    {
      header: t('date'),
      sortKey: 'timestamp',
      renderCell: (entry) => (
        <Typography variant="body2">
          {format(new Date(entry.timestamp), 'd.M.yyyy', { locale: dateFnsLocale() })}
        </Typography>
      ),
    },
    {
      header: t('employee'),
      sortKey: 'employee',
      renderCell: (entry) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UserAvatar
            name={entry.employeeName ?? '?'}
            size={28}
          />
          <Typography variant="body2">
            {entry.employeeName ?? t('unknownEmployee')}
          </Typography>
        </Box>
      ),
    },
    {
      header: t('type'),
      sortKey: 'type',
      renderCell: (entry) => (
        <Chip
          label={t(`entryType.${entry.type}`)}
          color={entryTypeColor[entry.type]}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      header: t('clockInTime'),
      renderCell: (entry) => (
        <Typography variant="body2" fontFamily="monospace">
          {format(new Date(entry.timestamp), 'HH:mm', { locale: dateFnsLocale() })}
        </Typography>
      ),
    },
    {
      header: t('notes'),
      renderCell: (entry) => (
        <Typography variant="body2" color="text.secondary">{entry.note ?? '—'}</Typography>
      ),
    },
  ];

  if (isError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void refetch()}>
            {tc('errors.refresh')}
          </Button>
        }
      >
        {t('errorLoadingTimeEntries')}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          type="date"
          size="small"
          label={t('startDate')}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: 160 }}
        />
        <TextField
          type="date"
          size="small"
          label={t('endDate')}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: 160 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t('employee')}</InputLabel>
          <Select
            value={employeeFilter}
            label={t('employee')}
            onChange={(e: SelectChangeEvent) => setEmployeeFilter(e.target.value)}
          >
            <MenuItem value="all">{t('allEmployees')}</MenuItem>
            {mechanics.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.firstName} {m.lastName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      {isLoading ? (
        <Stack spacing={1}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
          ))}
        </Stack>
      ) : (
        <DataTable<TimeEntry>
          columns={columns}
          data={entries}
          getRowKey={(entry) => entry.id}
          sortCompareFn={sortCompareFn}
          defaultSortKey="timestamp"
          defaultSortDirection="desc"
          emptyMessage={t('noTimeEntries')}
          ariaLabel={t('workHours')}
          renderMobileCard={(entry) => (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <UserAvatar name={entry.employeeName ?? '?'} size={28} />
                  <Typography variant="body2" fontWeight={500}>
                    {entry.employeeName ?? t('unknownEmployee')}
                  </Typography>
                </Box>
                <Chip
                  label={t(`entryType.${entry.type}`)}
                  color={entryTypeColor[entry.type]}
                  size="small"
                  variant="outlined"
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {format(new Date(entry.timestamp), 'd.M.yyyy', { locale: dateFnsLocale() })}
                </Typography>
                <Typography variant="body2" fontFamily="monospace">
                  {format(new Date(entry.timestamp), 'HH:mm', { locale: dateFnsLocale() })}
                </Typography>
              </Box>
              {entry.note && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {entry.note}
                </Typography>
              )}
            </Box>
          )}
        />
      )}
    </Box>
  );
}
